import { WrappedRouterLink } from '@/modules/common/components/WrappedRouterLink';
import { createTheme, ThemeOptions } from '@mui/material/styles';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from '../../tailwind.config';
import { colorPalette } from './colors';

const fullConfig = resolveConfig(tailwindConfig);

export const createMydraTheme = (rootElement: HTMLElement) =>
  createTheme({
    palette: colorPalette,
    typography: {
      fontFamily: 'Inter, sans-serif',
      h1: {
        fontSize: 24,
        fontWeight: 600,
        lineHeight: '130%',
        color: '#000000',
      },
      h2: {
        fontSize: 22,
        fontWeight: 500,
        lineHeight: '130%',
        color: '#000000',
      },
      h3: {
        fontSize: 20,
        fontWeight: 600,
        lineHeight: '130%',
        color: '#000000',
      },
      h4: {
        fontSize: 18,
        fontWeight: 600,
        lineHeight: '130%',
        color: '#000',
        letterSpacing: '-0.36px',
      },
      h5: {
        fontSize: 16,
        fontWeight: 600,
        lineHeight: '130%',
        color: '#000',
      },
      subtitle1: {
        fontSize: 18,
        lineHeight: '140%',
        color: '#000',
      },
      subtitle2: {
        fontSize: 14,
        fontWeight: 600,
        lineHeight: '130%',
        color: '#000',
      },
      body1: {
        fontSize: 16,
        lineHeight: '140%',
        color: '#000',
      },
      body2: {
        fontSize: 14,
        lineHeight: '140%',
      },
      caption: {
        fontSize: 12,
        lineHeight: '140%',
      },
    },
    components: {
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiPopover: {
        defaultProps: {
          container: rootElement,
        },
        styleOverrides: {
          root: {
            '& .MuiBackdrop-root': {
              backgroundColor: 'transparent',
            },
            '& .MuiList-root': {
              padding: 0,
            },
            '& .MuiMenuItem-root': {
              borderBottom: '1px solid',
              borderColor: fullConfig.theme.colors['dividers'],
              padding: '8px 16px',
              '&:last-child': {
                borderBottom: 'none',
              },
            },
          },
          paper: {
            boxShadow: fullConfig.theme.boxShadow['user-menu'],
            border: '1px solid',
            borderColor: fullConfig.theme.colors['dividers'],
            borderRadius: 8,
          },
        },
      },
      MuiPopper: {
        defaultProps: {
          container: rootElement,
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          size: 'small',
          classes: {
            root: 'normal-case rounded-lg font-medium text-base p-4 shadow-none',
            sizeSmall: 'px-3 py-2 text-sm',
            contained: 'shadow-none hover:shadow-none',
            text: 'hover:opacity-100',
            outlined: 'border-dividers',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'capitalize',
            fontSize: 16,
            fontWeight: 600,
            color: colorPalette.text.primary,
            paddingLeft: 0,
            paddingRight: 0,
            paddingBlock: 8,
            minHeight: 'auto',
          },
          icon: {
            minHeight: 'auto',
          },
        },
      },
      MuiTabs: {
        defaultProps: {
          classes: {
            root: 'border-b border-dividers',
            flexContainer: 'gap-10',
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
          square: true,
        },
      },
      MuiModal: {
        defaultProps: {
          container: rootElement,
        },
        styleOverrides: {
          root: {
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(51, 36, 76, 0.40)', // mydra-dark-purple with 40% opacity
            },
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          classes: {
            root: 'rounded-lg',
          },
        },
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              backgroundColor: fullConfig.theme.colors['gray-background'],
            },
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          container: rootElement,
        },
        styleOverrides: {
          root: {
            '& .MuiDialog-paper': {
              paddingBlock: '24px',
              paddingInline: '16px',
              paddingBlockEnd: '12px',
              borderRadius: '12px',
              maxWidth: 'none',
              width: 'auto',
              margin: '0',
              position: 'relative',
            },
            '& .MuiDialogTitle-root': {
              padding: '0',
              marginBottom: '6px',
              '& h2': {
                fontSize: '1.5rem',
                lineHeight: '2rem',
                fontWeight: '700',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              },
              '& h3': {
                fontSize: '0.875rem',
                lineHeight: '1.5rem',
                color: 'text.secondary',
              },
            },
            '& .MuiDialogContent-root': {
              padding: '0',
              marginTop: '24px',
              overflow: 'hidden',
            },
            '& .MuiDialogActions-root': {
              padding: '0',
              marginTop: '24px',
            },
          },
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          classes: 'text-2xl',
        },
      },
      MuiSnackbar: {
        defaultProps: {
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        },
        styleOverrides: {
          root: {
            backgroundColor: 'white',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: colorPalette.text.primary,
            fontWeight: 500,
            fontSize: '14px',
            marginBottom: '8px',
          },
        },
      },
      MuiLink: {
        defaultProps: {
          component: WrappedRouterLink,
        },
      },
      MuiButtonBase: {
        defaultProps: {
          LinkComponent: WrappedRouterLink,
        },
      },
      MuiSwitch: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiRadio: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiCheckbox: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '.MuiTableCell-head': {
              fontWeight: 400,
            },
            '.Mui-TableHeadCell-Content': {
              justifyContent: 'space-between',
              gap: '.5rem',
            },
            '.MuiTableCell-alignRight .Mui-TableHeadCell-Content': {
              justifyContent: 'end',
            },
            '.MuiTableCell-alignCenter .Mui-TableHeadCell-Content': {
              justifyContent: 'center',
            },
            '.Mui-TableHeadCell-Content-Labels': {
              gap: '.5rem',
            },
            '.MuiTableCell-alignRight .Mui-TableHeadCell-Content-Actions': {
              order: -1,
            },
            '.MuiTableRow-head': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            '.MuiTableCell-body': {
              color: '#696074',
              backgroundColor: colorPalette.background.default,
            },
            '.Mui-TableBodyCell-DetailPanel': {
              height: 'auto',
              backgroundColor: colorPalette.background.default,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colorPalette.background.paper,
            border: 'none',
            borderRadius: 8,
            boxShadow: fullConfig.theme.boxShadow['user-menu'],
            fontSize: 12,
            padding: 8,
            color: colorPalette.text.primary,
          },
          arrow: {
            color: colorPalette.background.paper,
          },
        },
      },
      MuiDateCalendar: {
        styleOverrides: {
          root: {
            '& .MuiDayCalendar-weekDayLabel': {
              color: colorPalette.text.disabled,
            },
            '& .MuiPickersArrowSwitcher-button': {
              color: colorPalette.primary.main,
            },
            '& .MuiPickersDay-root.Mui-selected, & .MuiPickersDay-root.Mui-selected:focus':
              {
                backgroundColor: colorPalette.secondary.main,
              },
            '& .MuiPickersDay-root.Mui-selected:hover': {
              backgroundColor: colorPalette.secondary.dark,
            },
            '& .MuiPickersDay-root.MuiPickersDay-today': {
              borderColor: colorPalette.secondary.main,
            },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: colorPalette.primary.main,
              '&:hover': {
                backgroundColor: colorPalette.primary.light,
              },
            },
          },
        },
      },
    },
  } as ThemeOptions);
